/**
 * Every store read this domain makes, in one file — the same rule as `channel`.
 * `index.ts` reads through these imperatively, `hooks/*` reactively; neither names a reducer
 * key of its own.
 *
 * Reducer keys used: `saleDataStore`, `itemsStore`, `businessStore`. All three exist in both
 * apps, and every field read here is declared on both platforms' slices.
 */
/**
 * The shared empties, for the "not loaded yet" case these three selectors all have.
 *
 * 🔴 **`?? {}` / `?? []` inline is an infinite render loop, not a wasted render.** `useSelector`
 * compares by IDENTITY, so a fallback literal is a brand-new value on every call and every
 * store notification looks like a change. A consumer that derives state from one in an effect
 * then feeds itself: the kiosk payment hook rebuilds its record, the engine re-seeds on the new
 * record, the re-seed dispatches, the dispatch re-runs this selector, and the next value is new
 * again. That spins at 100% CPU — `/kiosk/payment` hung the web test suite indefinitely, and it
 * reaches a browser the moment a sale is absent under a mounted screen (a session reset clears
 * one as a privacy control).
 *
 * All three are read by `useSelector` from a dozen hooks across loyalty, discounts, catalog,
 * cart and channel, so the hazard was never payment's alone.
 *
 * ⚠️ **They are SHARED, so nothing may mutate what these selectors return on a miss.** Every
 * consumer today reads them (`map`/`filter`/`find`); a writer must copy first. Deliberately not
 * frozen, matching `kiosk/payment/selectors`' `NONE` and `kiosk/config/selectors`'
 * `NO_MODE_IDS` — one convention for one hazard.
 */
import { quantityIndex } from "./lineModel";

const NO_SALE: Record<string, any> = {};
const NO_ITEMS: any[] = [];
const NO_ITEMS_BY_ID: Record<string, any> = {};

export function selectSaleData(state: any): any {
  return state?.saleDataStore?.saleData ?? NO_SALE;
}

export function selectSaleItems(state: any): any[] {
  return state?.saleDataStore?.saleData?.saleItems ?? NO_ITEMS;
}

export function selectItemsById(state: any): Record<string, any> {
  return state?.itemsStore?.itemsData ?? NO_ITEMS_BY_ID;
}

let optionSetsSource: any;
let optionSetsIndex: Record<string, any> = {};

export function selectOptionSetsById(state: any): Record<string, any> {
  const optionSets = state?.catalogStore?.optionSetData;
  if (optionSets === optionSetsSource) return optionSetsIndex;
  optionSetsSource = optionSets;
  optionSetsIndex = Array.isArray(optionSets)
    ? Object.fromEntries(
        optionSets.filter((set: any) => set?.mid).map((set: any) => [set.mid, set]),
      )
    : (optionSets ?? {});
  return optionSetsIndex;
}

export function selectSelectedChannel(state: any): any {
  const channel = state?.saleDataStore?.selectedChannelData;
  return channel?.channelId ? channel : undefined;
}

/** Sale-level fields the POS copies onto every line it builds. */
export function selectSaleItemContext(state: any): {
  channelId?: string;
  channelDefId?: string;
  channelName?: string;
  serviceLabel?: any;
  itemDiscountList?: any[];
  billDiscountList?: any[];
  createTickets?: boolean;
} {
  const saleStore = state?.saleDataStore;
  const channel = saleStore?.selectedChannelData;
  return {
    channelId: saleStore?.saleData?.channelId || channel?.channelId,
    channelDefId: channel?.channelDefId,
    channelName: channel?.name,
    serviceLabel: saleStore?.saleData?.serviceLabel ?? {},
    itemDiscountList: saleStore?.itemDiscountList ?? [],
    billDiscountList: saleStore?.billDiscountList ?? [],
    createTickets: saleStore?.createTickets ?? false,
  };
}

/**
 * The branch the order is for: the ordering context's one read (NEW_ORDER_V2_PLAN.md §5), which is
 * the session's branch unless a chosen-branch flow (the call centre) has set one.
 */
export { selectOrderingBranchId as selectBranchId } from "../../orderingContext/selectors";

/**
 * `itemId → quantity in the cart`, memoised ON THE STATE rather than per component.
 *
 * The same identity-keyed module cache `selectOptionSetsById` above uses, and for a sharper
 * reason: this is what every card on a 200-item menu reads. Built once per cart change and
 * shared by every caller in the same store notification, so `useItemQuantity` below it can be
 * a Map lookup that returns a NUMBER — which is what lets react-redux bail out of every card
 * whose count did not move.
 *
 * ⚠️ **Do not return this Map from a `useSelector`.** Its identity changes on every cart
 * mutation, so a component that subscribes to the Map itself re-renders on every add no matter
 * what it does with it — that is `useCartQuantities`' documented contract and the reason
 * `useItemQuantity` exists beside it.
 */
let quantitySaleItems: any;
let quantityItemsById: any;
let quantityIndexCache: Map<string, number> = new Map();

export function selectQuantityIndex(state: any): Map<string, number> {
  const saleItems = selectSaleItems(state);
  const itemsById = selectItemsById(state);
  if (saleItems === quantitySaleItems && itemsById === quantityItemsById) {
    return quantityIndexCache;
  }
  quantitySaleItems = saleItems;
  quantityItemsById = itemsById;
  quantityIndexCache = quantityIndex(saleItems, itemsById);
  return quantityIndexCache;
}

/** One card's count. A PRIMITIVE, so `useSelector` can compare it and bail out. */
export function selectItemQuantity(state: any, itemId: string | undefined): number {
  if (!itemId) return 0;
  return selectQuantityIndex(state).get(itemId) ?? 0;
}
